import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { formatNZDateTime } from "../../utils/timezone";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Loader2, Mail, Phone, MapPin, Calendar, User, AlertCircle, ExternalLink, Copy, Eye } from "lucide-react";

/**
 * Unified customer detail viewer for the Admin Customers tab.
 * Works for BOTH registered (customer_id UUID) and guest
 * (customer_id = "guest-email:<...>" | "guest-mobile:<...>") users.
 * Guests never have login stats / orders / stored password; those
 * fields render as "Not available — guest customer" instead of erroring.
 */
export default function AdminCustomerDetailDialog({ open, customerId, token, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !customerId) return;
    setLoading(true);
    setError(null);
    setData(null);
    axios
      .get(`${API}/admin/customer-detail/${encodeURIComponent(customerId)}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Failed to load customer"))
      .finally(() => setLoading(false));
  }, [open, customerId, token]);

  const isGuest = data?.is_guest;
  const NotAvailable = () => (
    <span className="text-xs text-muted-foreground italic">Not available — guest customer</span>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl w-[96vw] max-h-[92vh] overflow-y-auto"
        data-testid="admin-customer-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {data?.name || (loading ? "Loading..." : "Customer")}
            {data && (
              isGuest ? (
                <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50" data-testid="detail-type-badge">
                  Guest / Unregistered
                </Badge>
              ) : (
                <Badge variant="outline" className="border-blue-300 text-blue-700 bg-blue-50" data-testid="detail-type-badge">
                  Registered
                </Badge>
              )
            )}
          </DialogTitle>
          <DialogDescription>
            {data?.registration_status || (loading ? "Fetching customer..." : "")}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 p-4 rounded bg-red-50 text-red-700 text-sm" data-testid="detail-error">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {data && !loading && (
          <div className="space-y-4">
            {/* Contact block */}
            <Card>
              <CardContent className="pt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground w-20">Name</span>
                  <span className="font-medium" data-testid="detail-name">{data.name || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground w-20">Email</span>
                  <span className="font-medium break-all" data-testid="detail-email">{data.email || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground w-20">Phone</span>
                  <span className="font-medium" data-testid="detail-phone">{data.phone || data.phone_number || "—"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground w-20">Location</span>
                  <span className="font-medium">
                    {data.address?.full_address || data.postcode || "—"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground w-20">First seen</span>
                  <span className="font-medium" data-testid="detail-first-seen">
                    {data.first_seen || data.created_at
                      ? formatNZDateTime(data.first_seen || data.created_at)
                      : "—"}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-muted-foreground w-20">Last activity</span>
                  <span className="font-medium" data-testid="detail-last-activity">
                    {data.last_activity || data.last_login
                      ? formatNZDateTime(data.last_activity || data.last_login)
                      : (isGuest ? <NotAvailable /> : "—")}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Activity summary */}
            <div className="grid grid-cols-3 gap-3 text-center">
              <Card className="bg-primary/5">
                <CardContent className="py-3">
                  <p className="text-2xl font-bold text-primary" data-testid="detail-total-requests">{data.total_requests || 0}</p>
                  <p className="text-xs text-muted-foreground">Total submissions</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="py-3">
                  {isGuest ? (
                    <NotAvailable />
                  ) : (
                    <>
                      <p className="text-2xl font-bold">{data.total_orders || 0}</p>
                      <p className="text-xs text-muted-foreground">Orders</p>
                    </>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardContent className="py-3">
                  {isGuest ? (
                    <NotAvailable />
                  ) : (
                    <>
                      <p className="text-2xl font-bold">{data.login_count || 0}</p>
                      <p className="text-xs text-muted-foreground">Logins</p>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Requests list */}
            <div>
              <h3 className="text-sm font-semibold mb-2">All submissions ({(data.requests || []).length})</h3>
              {(data.requests || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No submissions yet.</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {data.requests.map((r) => (
                    <Card key={r.request_id} data-testid={`detail-request-${r.request_id}`}>
                      <CardContent className="p-3 space-y-1 text-sm">
                        <div className="flex items-center justify-between gap-2">
                          <div>
                            <span className="font-mono text-xs text-primary font-semibold">
                              {r.request_number || r.request_id}
                            </span>
                            <span className="ml-2 text-xs text-muted-foreground">
                              {r.created_at ? formatNZDateTime(r.created_at) : ""}
                            </span>
                          </div>
                          <Badge
                            variant="outline"
                            className={
                              r.request_status === "active"
                                ? "border-green-300 text-green-700 bg-green-50"
                                : r.request_status === "expired"
                                ? "border-red-300 text-red-700 bg-red-50"
                                : "border-gray-300 text-gray-700 bg-gray-50"
                            }
                          >
                            {r.request_status}
                          </Badge>
                        </div>
                        <p className="text-xs">
                          <span className="text-muted-foreground">Service:</span>{" "}
                          {(r.selected_services && r.selected_services.length > 0
                            ? r.selected_services.join(", ")
                            : r.service_type || ""
                          ).replace(/_/g, " ")}
                          {" · "}
                          <span className="text-muted-foreground">Quotes:</span> {r.quotes_count ?? (r.quotes?.length || 0)}
                        </p>
                        {r.vehicle_info && (r.vehicle_info.rego || r.vehicle_info.make || r.vehicle_info.model) && (
                          <p className="text-xs text-muted-foreground">
                            Vehicle: {[r.vehicle_info.year, r.vehicle_info.make, r.vehicle_info.model].filter(Boolean).join(" ")}
                            {r.vehicle_info.rego ? ` · ${r.vehicle_info.rego}` : ""}
                          </p>
                        )}
                        {r.tyre_specs && r.tyre_specs.width && (
                          <p className="text-xs text-muted-foreground">
                            Tyre: {r.tyre_specs.width}/{r.tyre_specs.aspect_ratio}R{r.tyre_specs.rim_size}
                          </p>
                        )}
                        {r.address?.full_address && (
                          <p className="text-xs text-muted-foreground">Address: {r.address.full_address}</p>
                        )}
                        {/* Impersonation-lite: open the exact magic-link
                            comparison page the customer sees. Works for
                            guests AND registered — every request now has
                            an access_token. */}
                        {r.access_token && (
                          <div className="flex items-center gap-2 pt-2 mt-1 border-t border-dashed">
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => window.open(`/r/${r.access_token}`, "_blank", "noopener")}
                              data-testid={`detail-view-as-customer-${r.request_id}`}
                            >
                              <Eye className="h-3 w-3 mr-1" />
                              View as customer
                              <ExternalLink className="h-3 w-3 ml-1" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                const url = `${window.location.origin}/r/${r.access_token}`;
                                navigator.clipboard.writeText(url).then(
                                  () => toast.success("Magic link copied"),
                                  () => toast.error("Copy failed"),
                                );
                              }}
                              data-testid={`detail-copy-magic-link-${r.request_id}`}
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              Copy link
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
